/**
 * PermissionButton - MUI Button that respects the Admin -> Role Config permissions.
 *
 * When the logged-in role has not been granted the permission the button is still rendered
 * (visible), but it is disabled, greyed out and explains why through a tooltip.
 *
 * Usage:
 *   <PermissionButton permission={HOME_PERMISSIONS.AUDIT_TRAIL} onClick={...}>Audit Trail</PermissionButton>
 */
import React from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import { useRolePermissions, NO_ACCESS_TOOLTIP } from '../../common/RolePermissions';

// Muted styling that marks an action the current role is not allowed to use.
export const noAccessButtonStyle = {
    background: '#e5e7eb',
    backgroundImage: 'none',
    color: '#9ca3af',
    border: '1px dashed #9ca3af',
    boxShadow: 'none',
    '&&.Mui-disabled, &&.Mui-disabled:hover': {
        background: '#e5e7eb',
        backgroundImage: 'none',
        color: '#9ca3af',
        border: '1px dashed #9ca3af',
        boxShadow: 'none',
    },
};

const PermissionButton = React.forwardRef(({ permission, sx, disabled, iconOnly = false, children, ...rest }, ref) => {
    const { hasPermission } = useRolePermissions();
    const ButtonComponent = iconOnly ? IconButton : Button;

    if (hasPermission(permission)) {
        return (
            <ButtonComponent ref={ref} sx={sx} disabled={disabled} {...rest}>
                {children}
            </ButtonComponent>
        );
    }

    // The span keeps the tooltip working - MUI disables pointer events on disabled buttons.
    return (
        <Tooltip title={NO_ACCESS_TOOLTIP}>
            <span ref={ref} style={{ display: 'inline-flex', cursor: 'not-allowed' }}>
                <ButtonComponent {...rest} sx={[...(Array.isArray(sx) ? sx : [sx]), noAccessButtonStyle]} disabled>
                    {children}
                </ButtonComponent>
            </span>
        </Tooltip>
    );
});

export default PermissionButton;
